import { CSSProperties, useMemo, useState } from 'react';

import { ExternalProperty } from 'crono-fe-common/types/externalProperty';
import {
  ControlledPopover,
  OverlayTooltip,
  type OverlayProps,
} from 'components/Overlay';
import AddTagTooltip from '../tagTooltip';
import ExternalPropertyComponentRemovable from 'crono-fe-common/components/ExternalPropertyRemovable';
import { ReactComponent as TopArrowIcon } from 'crono-fe-common/icons/Top-Arrow.svg';
import { ReactComponent as BottomArrowIcon } from 'crono-fe-common/icons/Bottom-Arrow.svg';
import { ensureArray, getExternalPropertyLabels } from 'crono-fe-common/utils';
import { MultipleTagWrapper } from './style';
import { FittingTagChips } from './FittingTagChips';
import { Constants } from 'crono-fe-common/constants/constants';
import { cn } from 'utils/style-utils';

/** A tag in a table cell is at most 180px and shrinks with the cell. */
const fitToWidthChipStyle: CSSProperties = {
  minWidth: 0,
  maxWidth: 'min(180px, 100%)',
};

export interface TagToShow {
  externalPropertyId: number;
  option: string;
}
interface IProps {
  indexOfTag: number;
  handleClickTag?: (
    option: string | null,
    externalProperty: ExternalProperty,
    index: number,
  ) => void;
  strategyTagValue?: TagToShow | null;
  isEditable: boolean;
  option: string | null;
  tag: ExternalProperty;
  indexGeneralArray: number;
  showRemovable?: boolean;
  showAsPlus?: boolean;
  showMandatoryStar?: boolean;
  whiteBackground?: boolean;
  //If this is true for multiselect, if there is more than 1 option, it will show the first 2 and a plus
  show2AndPlus?: boolean;
  //With show2AndPlus, show as many options as fit in the available width instead of 2
  fitToWidth?: boolean;

  customStyle?: CSSProperties;

  // Externally controlled open state (used by TagsBoxShowAll to keep only one open at a time)
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;

  /** Pass it when the tag sits in a table cell. */
  overlayProps?: OverlayProps;
}

export const isString = (value: string | string[] | null): value is string =>
  typeof value === 'string';

const TagsBoxSingleTagShowAll = ({
  indexOfTag,
  handleClickTag,
  isEditable,
  strategyTagValue,
  option,
  tag,
  indexGeneralArray,
  showRemovable = true,
  showAsPlus = false,
  showMandatoryStar = false,
  whiteBackground = false,
  show2AndPlus = false,
  fitToWidth = false,
  customStyle,
  isOpen: isOpenProp,
  onOpen,
  onClose,
  overlayProps,
}: IProps) => {
  const isMultiple = tag.valueType === 'MultiSelect';
  const showMore = show2AndPlus && isMultiple;
  const parsedOptions = useMemo(() => {
    if (isMultiple) {
      return option?.split(Constants.multiselectOptionsDivider) ?? null;
    }
    return option;
  }, [option, isMultiple]);

  const [openTooltip, setOpenTooltip] = useState<boolean>(false);

  const isControlled = isOpenProp !== undefined;
  const setOpen = (value: boolean) => {
    if (isControlled) {
      if (value) {
        onOpen?.();
      } else {
        onClose?.();
      }
    } else {
      setOpenTooltip(value);
    }
  };

  const open = useMemo(() => {
    return (isControlled ? isOpenProp : openTooltip) && isEditable;
  }, [isControlled, isOpenProp, openTooltip, isEditable]);

  const [searchTagValue, setSearchTagValue] = useState<string>('');

  const tagToShow: ExternalProperty & { indexMatch: number[] } = useMemo(() => {
    //If there is no search just display all the tags
    if (!searchTagValue)
      return {
        ...tag,
        indexMatch: Array.from(
          { length: tag.options?.length ?? 0 },
          (_, index) => index,
        ),
      };

    return {
      ...tag,
      indexMatch: getExternalPropertyLabels(tag)
        .map((label, index) => {
          const bool = label
            .toLowerCase()
            .includes(searchTagValue.toLowerCase());
          if (bool) return index;
          return null;
        })
        .filter((val) => val !== null) as number[],
    };
  }, [searchTagValue, tag]);

  const getLabel = (option: string) => {
    return getExternalPropertyLabels(tag)[
      tag.options?.findIndex((property) => property === option) ?? 0
    ];
  };

  const firstTagForShowMore = useMemo(() => {
    if (!showMore) return null;
    //Considering the order of the options in the tagToShow, show the first 1
    const firstOption = tagToShow.options?.find((option) =>
      ensureArray(parsedOptions).includes(option),
    );
    const isThereSecondOption = parsedOptions?.[1] !== undefined;
    return (
      <ExternalPropertyComponentRemovable
        index={indexOfTag}
        disabled={!isEditable}
        value={firstOption ? getLabel(firstOption) : null}
        tooltipValue={firstOption ? getLabel(firstOption) : null}
        indexOption={
          firstOption ? tagToShow?.options?.indexOf(firstOption) : undefined
        }
        onClick={() => {
          setOpen(true);
        }}
        onClickRemove={() => {
          if (handleClickTag) {
            handleClickTag(
              firstOption === undefined ? null : firstOption,
              tagToShow,
              indexOfTag,
            );
          }
        }}
        customStyle={{
          ...(whiteBackground
            ? {
                backgroundColor: whiteBackground ? 'white' : undefined,
                borderRadius: whiteBackground ? 8 : undefined,
              }
            : {}),
          ...(!isThereSecondOption
            ? {
                maxWidth: 250,
              }
            : { maxWidth: 140 }),
          ...customStyle,
        }}
        showRemove={showRemovable && isEditable}
        nullValueText={
          showAsPlus ? null : (
            <span className="null-value-text">
              <p className="flex-1 truncate text-xs leading-5">
                {tagToShow.publicName ??
                  tagToShow.externalName ??
                  'Property ' + (indexOfTag + 1)}
                {showMandatoryStar && tagToShow.isMandatory && '*'}
              </p>
              {open ? (
                <TopArrowIcon className="tag-arrow" />
              ) : (
                <BottomArrowIcon className="tag-arrow" />
              )}
            </span>
          )
        }
        isBeingModified={open}
        searchTagValue={searchTagValue}
        setSearchTagValue={setSearchTagValue}
        onEsc={() => {
          setSearchTagValue('');
          setOpen(false);
        }}
        onEnter={() => {
          if (tagToShow.indexMatch.length <= 0) return;
          const option = tagToShow.options![tagToShow.indexMatch[0]];
          if (handleClickTag) {
            handleClickTag(option, tagToShow, indexOfTag);
            setSearchTagValue('');
            setOpen(false);
          }
        }}
      />
    );
  }, [showMore, parsedOptions, open, searchTagValue, tagToShow]);

  const secondTagForShowMore = useMemo(() => {
    if (!showMore) return null;
    //Considering the order of the options in the tagToShow, show the second one
    const firstOption = tagToShow.options?.find((option) =>
      ensureArray(parsedOptions).includes(option),
    );
    const secondOption = tagToShow.options?.find(
      (option) =>
        option !== firstOption && ensureArray(parsedOptions).includes(option),
    );
    if (secondOption === undefined) return null;
    return (
      <ExternalPropertyComponentRemovable
        index={indexOfTag}
        disabled={!isEditable}
        value={getLabel(secondOption)}
        tooltipValue={getLabel(secondOption)}
        indexOption={tagToShow?.options?.indexOf(secondOption)}
        onClick={() => {
          setOpen(true);
        }}
        onClickRemove={() => {
          if (handleClickTag) {
            handleClickTag(secondOption, tagToShow, indexOfTag);
          }
        }}
        customStyle={{
          ...(whiteBackground
            ? {
                backgroundColor: whiteBackground ? 'white' : undefined,
                borderRadius: whiteBackground ? 8 : undefined,
              }
            : {}),
          maxWidth: 140,
        }}
        showRemove={showRemovable && isEditable}
      />
    );
  }, [showMore, parsedOptions, tagToShow]);

  const additionalTags = useMemo(() => {
    if (!showMore || !parsedOptions) return null;
    if (isString(parsedOptions) || parsedOptions.length <= 2) return null;
    const firstOption = tagToShow.options?.find((option) =>
      ensureArray(parsedOptions).includes(option),
    );
    const secondOption = tagToShow.options?.find(
      (option) =>
        option !== firstOption && ensureArray(parsedOptions).includes(option),
    );
    const remainingOptions =
      tagToShow.options
        ?.filter(
          (option) =>
            option !== firstOption &&
            option !== secondOption &&
            ensureArray(parsedOptions).includes(option),
        )
        .map((option) => ({
          label: getLabel(option),
          option,
          indexOption: tagToShow.options?.indexOf(option) ?? 0,
        })) ?? [];
    return (
      <OverlayTooltip
        content={
          remainingOptions.length > 0 && (
            <div className="flex flex-col gap-1.5 w-fit max-w-60 py-0.5">
              {remainingOptions.map(({ label, option: opt, indexOption }) => (
                <ExternalPropertyComponentRemovable
                  key={opt}
                  index={indexOfTag}
                  disabled={true}
                  value={label}
                  tooltipValue={null}
                  indexOption={indexOption}
                  showRemove={false}
                  customStyle={{ maxWidth: '100%' }}
                />
              ))}
            </div>
          )
        }
        tone="light"
        delayDuration={400}
        overlayProps={overlayProps}
      >
        <div
          className="remaining-tags-button"
          onClick={() => {
            setOpen(true);
          }}
        >
          +{parsedOptions.length - 2}
        </div>
      </OverlayTooltip>
    );
  }, [showMore, parsedOptions, tagToShow, indexOfTag, overlayProps]);

  const shouldShowHoverChips =
    isMultiple && Array.isArray(parsedOptions) && parsedOptions.length > 1;

  const chipsContent = shouldShowHoverChips ? (
    <div className="flex flex-col gap-1.5 w-fit max-w-60 py-0.5">
      {tagToShow.options
        ?.filter((opt) => (parsedOptions as string[]).includes(opt))
        .map((opt) => (
          <ExternalPropertyComponentRemovable
            key={opt}
            index={indexOfTag}
            disabled={true}
            value={getLabel(opt)}
            tooltipValue={null}
            indexOption={tagToShow.options?.indexOf(opt)}
            showRemove={false}
            customStyle={{ maxWidth: '100%' }}
          />
        ))}
    </div>
  ) : null;

  const closeEditor = () => {
    setSearchTagValue('');
    setOpen(false);
  };

  const editor = (
    <AddTagTooltip
      toShow={tagToShow}
      handleClick={(option, externalProperty) => {
        setSearchTagValue('');
        if (handleClickTag) {
          handleClickTag(option, externalProperty, indexGeneralArray);
        }
        setOpen(false);
      }}
      indexExternalProperty={indexOfTag}
      currentOption={parsedOptions}
      useEmptySelection={isMultiple}
    />
  );

  const editorClassName =
    'max-h-75 max-w-100 overflow-hidden rounded-2xl py-3 pr-0 pl-3';

  if (
    showMore &&
    fitToWidth &&
    Array.isArray(parsedOptions) &&
    parsedOptions.length > 1
  ) {
    const parsedOptionSet = new Set(parsedOptions);
    const selectedOptions =
      tagToShow.options?.filter((opt) => parsedOptionSet.has(opt)) ?? [];
    return (
      <ControlledPopover
        open={open}
        onClose={closeEditor}
        content={editor}
        overlayProps={overlayProps}
        className={editorClassName}
        anchorClassName="flex w-full min-w-0"
      >
        <FittingTagChips
          options={selectedOptions}
          overlayProps={overlayProps}
          onMoreClick={() => setOpen(true)}
          renderChip={(opt) => (
            <ExternalPropertyComponentRemovable
              index={indexOfTag}
              disabled={!isEditable}
              value={getLabel(opt)}
              tooltipValue={getLabel(opt)}
              indexOption={tagToShow.options?.indexOf(opt)}
              onClick={() => setOpen(true)}
              onClickRemove={() => handleClickTag?.(opt, tagToShow, indexOfTag)}
              customStyle={fitToWidthChipStyle}
              tooltipWhenTruncated
              showRemove={showRemovable && isEditable}
            />
          )}
          renderHidden={(hiddenOptions) => (
            <div className="flex flex-col gap-1.5 w-fit max-w-60 py-0.5">
              {hiddenOptions.map((opt) => (
                <ExternalPropertyComponentRemovable
                  key={opt}
                  index={indexOfTag}
                  disabled={true}
                  value={getLabel(opt)}
                  tooltipValue={null}
                  indexOption={tagToShow.options?.indexOf(opt)}
                  showRemove={false}
                  customStyle={{ maxWidth: '100%' }}
                />
              ))}
            </div>
          )}
        />
      </ControlledPopover>
    );
  }

  if (showMore && !fitToWidth && !isString(parsedOptions)) {
    return (
      <ControlledPopover
        open={open}
        onClose={closeEditor}
        content={editor}
        overlayProps={overlayProps}
        className={editorClassName}
        anchorClassName="flex w-fit"
      >
        <MultipleTagWrapper>
          {firstTagForShowMore && firstTagForShowMore}
          {secondTagForShowMore && secondTagForShowMore}
          {additionalTags && additionalTags}
        </MultipleTagWrapper>
      </ControlledPopover>
    );
  }

  return (
    <OverlayTooltip
      content={shouldShowHoverChips && !open && chipsContent}
      tone="light"
      delayDuration={400}
      overlayProps={overlayProps}
    >
      <div className={cn(fitToWidth && 'flex max-w-full min-w-0')}>
        <ControlledPopover
          open={open}
          onClose={closeEditor}
          content={editor}
          overlayProps={overlayProps}
          className={editorClassName}
          anchorClassName={cn(fitToWidth && 'flex max-w-full min-w-0')}
        >
          <ExternalPropertyComponentRemovable
            index={indexOfTag}
            disabled={!isEditable}
            value={
              parsedOptions
                ? isString(parsedOptions)
                  ? getLabel(parsedOptions)
                  : parsedOptions.length === 1
                    ? getLabel(parsedOptions[0])
                    : `${parsedOptions.length} ${tag.publicName}`
                : null
            }
            tooltipValue={
              shouldShowHoverChips
                ? ''
                : parsedOptions
                  ? isString(parsedOptions)
                    ? getLabel(parsedOptions)
                    : parsedOptions.length === 1
                      ? getLabel(parsedOptions[0])
                      : null
                  : null
            }
            indexOption={ensureArray(parsedOptions).map((singleOption) =>
              tagToShow?.options?.indexOf(singleOption ?? ''),
            )}
            onClick={() => {
              setOpen(true);
            }}
            onClickRemove={() => {
              if (handleClickTag) {
                handleClickTag(null, tagToShow, indexOfTag);
              }
            }}
            customStyle={{
              ...(whiteBackground
                ? {
                    backgroundColor: whiteBackground ? 'white' : undefined,
                    borderRadius: whiteBackground ? 8 : undefined,
                  }
                : {}),
              ...(fitToWidth && fitToWidthChipStyle),
              ...customStyle,
            }}
            tooltipWhenTruncated={fitToWidth}
            showRemove={showRemovable && isEditable}
            nullValueText={
              showAsPlus ? null : (
                <span className="null-value-text">
                  <p className="flex-1 truncate text-xs leading-5">
                    {tagToShow.publicName ??
                      tagToShow.externalName ??
                      'Property ' + (indexOfTag + 1)}
                    {showMandatoryStar && tagToShow.isMandatory && '*'}
                  </p>
                  {open ? (
                    <TopArrowIcon className="tag-arrow" />
                  ) : (
                    <BottomArrowIcon className="tag-arrow" />
                  )}
                </span>
              )
            }
            isBeingModified={open}
            searchTagValue={searchTagValue}
            setSearchTagValue={setSearchTagValue}
            onEsc={() => {
              setSearchTagValue('');
              setOpen(false);
            }}
            onEnter={() => {
              if (tagToShow.indexMatch.length <= 0) return;
              const option = tagToShow.options![tagToShow.indexMatch[0]];
              if (handleClickTag) {
                handleClickTag(option, tagToShow, indexOfTag);
                setSearchTagValue('');
                setOpen(false);
              }
            }}
          />
        </ControlledPopover>
      </div>
    </OverlayTooltip>
  );
};

export default TagsBoxSingleTagShowAll;
